import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { JobChip } from '@/components/ui/job-chip';
import { Shield, Microscope, Map, Sprout, ExternalLink } from 'lucide-react';
import forestersBg from '@/assets/foresters-bg.webp';
import ScrollReveal from '@/components/animations/ScrollReveal';
const JobsSection = () => {
  const {
    t
  } = useLanguage();
  const [contentOpacity, setContentOpacity] = useState(0);
  const [bgVisible, setBgVisible] = useState(false);
  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const section = document.getElementById('jobs-section');
        const content = document.getElementById('jobs-content');
        const lastCard = document.getElementById('last-job-card');
        if (!section || !content) return;
        const sectionRect = section.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const contentTop = contentRect.top;
        const vh = window.innerHeight;

        // Show background only when Jobs section is near the viewport
        const showThreshold = vh + 100;
        setBgVisible(sectionRect.top < showThreshold);

        // Fade-in: when content enters viewport from below
        const fadeInStart = vh * 0.95;
        const fadeInEnd = vh * 0.5;

        // Fade-out: based on last card position touching top of viewport
        const lastCardTop = lastCard ? lastCard.getBoundingClientRect().top : contentTop;
        const lastCardHeight = lastCard ? lastCard.getBoundingClientRect().height : 200;
        const fadeOutStart = 0;
        const fadeOutEnd = -lastCardHeight * 0.8;
        let opacity = 1;
        if (contentTop > fadeInStart) {
          opacity = 0;
        } else if (contentTop > fadeInEnd) {
          opacity = (fadeInStart - contentTop) / (fadeInStart - fadeInEnd);
        } else if (lastCardTop > fadeOutStart) {
          opacity = 1;
        } else if (lastCardTop > fadeOutEnd) {
          opacity = (lastCardTop - fadeOutEnd) / (fadeOutStart - fadeOutEnd);
        } else {
          opacity = 0;
        }
        setContentOpacity(Math.max(0, Math.min(1, opacity)));
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  const profiles = [{
    icon: Shield,
    title: t('jobs.ranger.title'),
    desc: t('jobs.ranger.desc'),
    personalities: [t('jobs.ranger.personality1'), t('jobs.ranger.personality2')],
    tasks: [t('jobs.ranger.task1'), t('jobs.ranger.task2')],
    color: 'bg-forest-700',
    link: 'https://www.plt.org/wp-content/uploads/pdf/PLT_GJ_FactSheet_Forester.pdf'
  }, {
    icon: Microscope,
    title: t('jobs.scientist.title'),
    desc: t('jobs.scientist.desc'),
    personalities: [t('jobs.scientist.personality1'), t('jobs.scientist.personality2')],
    tasks: [t('jobs.scientist.task1'), t('jobs.scientist.task2')],
    color: 'bg-teal-500',
    link: 'https://www.plt.org/wp-content/uploads/pdf/PLT_GJ_FactSheet_ForestryTech.pdf'
  }, {
    icon: Map,
    title: t('jobs.tech.title'),
    desc: t('jobs.tech.desc'),
    personalities: [t('jobs.tech.personality1'), t('jobs.tech.personality2')],
    tasks: [t('jobs.tech.task1'), t('jobs.tech.task2')],
    color: 'bg-sky-500',
    link: 'https://www.plt.org/wp-content/uploads/pdf/PLT_GJ_FactSheet_GISSpecialist.pdf'
  }, {
    icon: Sprout,
    title: t('jobs.silviculture.title'),
    desc: t('jobs.silviculture.desc'),
    personalities: [t('jobs.silviculture.personality1'), t('jobs.silviculture.personality2')],
    tasks: [t('jobs.silviculture.task1'), t('jobs.silviculture.task2')],
    color: 'bg-sunset-500',
    link: 'https://www.plt.org/wp-content/uploads/pdf/PLT_GJ_FactSheet_SilvicultureTech.pdf'
  }];
  return <section id="jobs-section" className="relative min-h-[180vh]">
      {/* Fixed background image - only visible when Jobs section is near */}
      <div className="fixed inset-0 z-0 h-screen bg-cover bg-[85%_center] md:bg-center bg-no-repeat transition-opacity duration-300" style={{
      backgroundImage: `url(${forestersBg})`,
      opacity: bgVisible ? 1 : 0,
      pointerEvents: bgVisible ? 'auto' : 'none'
    }} />
      
      
      {/* Initial spacer - clean image first */}
      <div className="h-[60vh]" aria-hidden="true" />
      
      {/* Content that fades in and out */}
      <div id="jobs-content" className="relative z-10 py-section-mobile md:py-section-desktop" style={{
      opacity: contentOpacity,
      transition: 'opacity 0.15s ease-out'
    }}>
        <div className="container mx-auto px-4 lg:px-lg">
          <div className="flex flex-col items-center">
            {/* Title */}
            <div className="max-w-xl bg-forest-900/85 backdrop-blur-sm rounded-2xl p-lg mb-2xl px-[15px] text-center mx-[17px]">
              <ScrollReveal delay={80}>
                <h2 className="text-h2 md:text-[40px] font-bold text-sand-50 mb-md">
                  {t('jobs.title')}
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={160}>
                <p className="text-body text-sand-100/90 max-prose">
                  {t('jobs.subtitle')}
                </p>
              </ScrollReveal>
            </div>

            {/* Cards */}
            <div className="grid sm:grid-cols-2 gap-lg max-w-4xl">
              {profiles.map((profile, index) => <ScrollReveal key={index} delay={index * 80}>
                  <Card id={index === profiles.length - 1 ? 'last-job-card' : undefined} className="group bg-card border-0 shadow-card rounded-xl hover-lift overflow-hidden h-full">
                    <CardContent className="pt-xl px-lg pb-lg">
                      <div className={`w-12 h-12 rounded-lg ${profile.color} flex items-center justify-center mb-md`}>
                        <profile.icon className="h-6 w-6 text-sand-50" />
                      </div>
                      <h3 className="text-h3 font-bold text-forest-900 mb-xs group-hover:text-sunset-600 transition-colors duration-fast">
                        {profile.title}
                      </h3>
                      <p className="text-small text-stone-500 mb-md">
                        {profile.desc}
                      </p>
                      <div className="space-y-sm">
                        
                        <div className="flex flex-wrap gap-xs mx-0">
                          {profile.tasks.map((task, i) => <JobChip key={`t-${i}`} className="bg-sand-200/80 text-stone-600">{task}</JobChip>)}
                        </div>
                      </div>
                      <a href={profile.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-md text-small font-medium text-forest-600 hover:text-sunset-600 transition-colors">
                        Learn more
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </CardContent>
                  </Card>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Spacer for clean image reveal */}
      <div className="h-[80vh]" aria-hidden="true" />
    </section>;
};
export default JobsSection;